import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { redirect } from "react-router-dom";

import type { ID } from "@constants";
import type { IOption, IRole } from "@/interfaces";
import { useUIStore } from "@/stores/uiStore";
import {
  IAuthStepEnum,
  useAuthStore,
} from "@/template/authManagement/authStore";
import { authManagement } from "@routes/authManagement/path";

export interface IPermissionAction {
  canActive: boolean;
  canEdit: boolean;
  canAdd: boolean;
  canDelete: boolean;
}

export type IPermissionModuleName = "station" | "devices" | "rules";

export type IPermission = Record<IPermissionModuleName, IPermissionAction>;

export interface IPermissionObj {
  name: IPermissionModuleName;
  action: keyof IPermissionAction;
}

interface IUserState {
  isAuthenticated: boolean;
  role: IRole | null;
  roles: IOption<IRole>[];
  token: string | null;
  id: ID;
}

interface IUserStateFn {
  login: (token: string, role: IRole, roles: IOption<IRole>[], id: ID) => void;
  logout: () => void;
  changeRole: (role: IRole) => void;
}

const userInitState: IUserState = {
  id: "",
  isAuthenticated: false,
  role: null,
  roles: [],
  token: "",
};

export const useUserStore = create<IUserState & IUserStateFn>()(
  subscribeWithSelector(
    devtools(
      persist(
        (set) => ({
          ...userInitState,
          login: (
            token: string,
            role: IRole | null,
            roles: IOption<IRole>[],
            id: ID,
          ) => {
            set({
              isAuthenticated: true,
              token,
              role,
              roles,
              id,
            });
          },
          logout: () => {
            set({ ...userInitState });
            useUIStore.setState({ pageMode: "index" });
            useAuthStore.getState().resetState();
            redirect(authManagement.auth.link(IAuthStepEnum.LOGIN));
          },
          changeRole(role) {
            set({
              role,
            });
          },
        }),
        {
          name: "userState",
          partialize: (state) => ({
            role: state.role,
            token: state.token,
            id: state.id,
          }),
        },
      ),
      {
        enabled: !import.meta.env.PROD,
        name: "userState",
      },
    ),
  ),
);

const initialState = useUserStore.getState();

export const userStorFn = {
  login: useUserStore.getState().login,
  logout: useUserStore.getState().logout,
  changeRole: useUserStore.getState().changeRole,
};

if (initialState.token) {
  useUserStore.setState({ isAuthenticated: true });
}
